/**
 * Geohash encoder for player-reported location (e.g. proof-of-play heartbeats).
 *
 * Precision guide (approximate cell size):
 *   5 → ~4.9 km × 4.9 km
 *   6 → ~1.2 km × 0.6 km
 *   7 → ~153 m  × 153 m   ← default for CTA scan events; player proof-of-play heartbeats (coarser = less GPS jitter)
 *   8 → ~38 m   × 19 m
 *   9 → ~4.9 m × 4.9 m
 *
 * Pure implementation — no external dependency, compatible with Cloudflare Workers.
 */

const BASE32 = '0123456789bcdefghjkmnpqrstuvwxyz'

/**
 * Default precision used when writing `playerGeohash` on analytics events that store geolocation.
 * Precision 7 ≈ 153 m × 153 m cell — fine enough for analytics without exposing
 * exact street-level coordinates in aggregated exports.
 */
export const GEOHASH_DEFAULT_PRECISION = 7

/**
 * Precision for `playerGeohash` on proof-of-play playback heartbeats (player-reported GPS).
 * Kept at {@link GEOHASH_DEFAULT_PRECISION} so small GPS errors do not hop geohash cells as often;
 * increase only if you need finer placement and accept noisier aggregates.
 */
export const GEOHASH_PLAYER_HEARTBEAT_PRECISION = 7

/**
 * Encode a WGS-84 latitude/longitude pair to a geohash string.
 *
 * @param lat       Latitude in decimal degrees (−90 … 90)
 * @param lng       Longitude in decimal degrees (−180 … 180)
 * @param precision Number of base-32 characters (1–12). Default: {@link GEOHASH_DEFAULT_PRECISION}.
 * @returns         Lower-case geohash string of the requested length.
 */
export function latLngToGeohash(lat: number, lng: number, precision = GEOHASH_DEFAULT_PRECISION): string {
  let minLat = -90
  let maxLat = 90
  let minLng = -180
  let maxLng = 180

  let hash = ''
  let bits = 0
  let bitsTotal = 0
  let hashValue = 0
  let isEven = true

  while (hash.length < precision) {
    if (isEven) {
      const midLng = (minLng + maxLng) / 2
      if (lng >= midLng) {
        hashValue = (hashValue << 1) | 1
        minLng = midLng
      }
      else {
        hashValue = hashValue << 1
        maxLng = midLng
      }
    }
    else {
      const midLat = (minLat + maxLat) / 2
      if (lat >= midLat) {
        hashValue = (hashValue << 1) | 1
        minLat = midLat
      }
      else {
        hashValue = hashValue << 1
        maxLat = midLat
      }
    }

    isEven = !isEven
    bits++
    bitsTotal++

    if (bits === 5) {
      hash += BASE32[hashValue]
      bits = 0
      hashValue = 0
    }
  }

  return hash
}

export interface IGeohashBoundingBox {
  minLat: number
  maxLat: number
  minLng: number
  maxLng: number
}

/**
 * Decode a geohash string to its latitude/longitude bounding box (WGS-84).
 * Inverse of {@link latLngToGeohash} for the same precision.
 *
 * @returns `null` when the string is empty or contains a non-base32 character.
 */
export function geohashToBoundingBox(geohash: string): IGeohashBoundingBox | null {
  const h = geohash.trim().toLowerCase()
  if (h.length === 0) {
    return null
  }
  let minLat = -90
  let maxLat = 90
  let minLng = -180
  let maxLng = 180
  let isEven = true
  for (let i = 0; i < h.length; i++) {
    const idx = BASE32.indexOf(h[i]!)
    if (idx < 0) {
      return null
    }
    for (let mask = 16; mask >= 1; mask >>= 1) {
      if (isEven) {
        const mid = (minLng + maxLng) / 2
        if ((idx & mask) !== 0) {
          minLng = mid
        }
        else {
          maxLng = mid
        }
      }
      else {
        const mid = (minLat + maxLat) / 2
        if ((idx & mask) !== 0) {
          minLat = mid
        }
        else {
          maxLat = mid
        }
      }
      isEven = !isEven
    }
  }
  return { minLat, maxLat, minLng, maxLng }
}

/** Center of the geohash cell (WGS-84). */
export function geohashToCenter(geohash: string): { lat: number, lng: number } | null {
  const b = geohashToBoundingBox(geohash)
  if (b == null) {
    return null
  }
  return {
    lat: (b.minLat + b.maxLat) / 2,
    lng: (b.minLng + b.maxLng) / 2
  }
}
